import { useTitanApp } from "@/hooks/useTitanApp";
import { useEffect, useState } from "react";
import { AuditTimelineEvents } from "@/enum/AuditTimelineEvents";
import { User, Calendar } from "lucide-react";
export interface ConsultationTimelineProps {
  isDarkMode?: boolean;
  className?: string;
}

interface TimelineEvent {
  id: string;
  timestamp: string;
  time: string;
  date: string;
  actor: string;
  actorName: string;
  event: string;
  description?: string;
  type: string;
  transcription?: string;
}

export const TypeName = Object.freeze({
  1: "Prescribed",
  2: "Payment",
  3: "Referral Letter",
  4: "Booking",
  5: "Vitals Recorded",
  6: "Vaccinations",
  7: "General",
  8: "Document",
  9: "Image",
  14: "Tasks",
  15: "Treatment Plan",
});

export const useAuditTimelineData = () => {
  const { patientState, appState } = useTitanApp();
  const { consultationAuditTimeline, consultationId, auditTimelineLoader } =
    patientState || {};
  const { darkMode } = appState || {};
  const isDarkMode = darkMode ?? false;

  const [events, setEvents] = useState<TimelineEvent[]>([]);

  const formatDate = (timestamp: string): string => {
    if (!timestamp) return "";
    try {
      const date = new Date(timestamp);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    } catch {
      return "";
    }
  };

  const formatTime = (timestamp: string): string => {
    if (!timestamp) return "";
    try {
      const date = new Date(timestamp);
      return date
        .toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
        .toUpperCase();
    } catch {
      return "";
    }
  };

  const getEventName = (eventCode: number): string => {
    return (
      AuditTimelineEvents[eventCode as keyof typeof AuditTimelineEvents] ||
      `Event ${eventCode}`
    );
  };

  const getTypeName = (
    actionCode: number | null,
    eventCode: number,
  ): string => {
    switch (actionCode) {
      case 1:
        return TypeName[1]; // "Prescribed"
      case 2:
        return TypeName[2]; // "Payment"
      case 3:
        return TypeName[3]; // "Referral Letter"
      case 4:
        return TypeName[4]; // "Booking"
      case 5:
        return TypeName[5]; // "Vitals Recorded"
      case 6:
        return TypeName[6]; // "Vaccinations"
      case 7:
        return TypeName[7]; // "General"
      case 8:
        return TypeName[8]; // "Document"
      case 13:
        return TypeName[9]; // "Image"
      case 14:
        return TypeName[14]; // "Tasks"
      case 15:
        return TypeName[15]; // "Treatment Plan"
      case null:
      case undefined:
        switch (eventCode) {
          case 1:
            return "pre-consult";
          case 2: // Consultation Started
          case 3: // Consultation Ended
          case 4: // Consultation Paused
          case 5: // Consultation Resumed
          case 6: // Consultation Canceled
          case 7: // FreeType Consultation
          case 8: // Manual Consultation
            return "consultation";
          case 9: // Template Added
          case 10: // Chat Prompt
          case 11: // Chat Response
          case 12: // Action Performed
          case 13: // Notes
          case 14: // Transcription
          case 15: // Feedback
          case 16: // Consultation Summary
          case 17: // Medications
          case 18: // Allergies
          case 19: // Medical Conditions
            return "system";
          default:
            return "system";
        }
      default:
        return `Action ${actionCode}`;
    }
  };
  const parseVitalsDescription = (value: string): string => {
    try {
      const parsed = JSON.parse(value);
      if (parsed?.vitalsDetails) {
        const vitals = parsed.vitalsDetails;
        const vitalEntries = [];

        if (vitals.heartRate?.Value) {
          vitalEntries.push(
            `Heart Rate: ${vitals.heartRate.Value} ${
              vitals.heartRate.Unit || "BPM"
            }`,
          );
        }
        if (vitals.weight?.Value) {
          vitalEntries.push(
            `Weight: ${vitals.weight.Value} ${vitals.weight.Unit || "kg"}`,
          );
        }
        if (
          vitals.bloodPressure?.Systolic?.Value ||
          vitals.bloodPressure?.Diastolic?.Value
        ) {
          const sys = vitals.bloodPressure.Systolic?.Value || "";
          const dia = vitals.bloodPressure.Diastolic?.Value || "";
          vitalEntries.push(`Blood Pressure: ${sys}/${dia} mmHg`);
        }
        if (vitals.temperature?.Value) {
          vitalEntries.push(
            `Temperature: ${vitals.temperature.Value} ${
              vitals.temperature.Unit || "°C"
            }`,
          );
        }
        if (vitals.respiratoryRate?.Value) {
          vitalEntries.push(
            `Respiratory Rate: ${vitals.respiratoryRate.Value} ${
              vitals.respiratoryRate.Unit || "breaths/min"
            }`,
          );
        }
        if (vitals.height?.Value) {
          vitalEntries.push(
            `Height: ${vitals.height.Value} ${vitals.height.Unit || "cm"}`,
          );
        }
        if (vitals.bmi?.Value) {
          vitalEntries.push(
            `BMI: ${vitals.bmi.Value} ${vitals.bmi.Unit || "kg/m²"}`,
          );
        }
        if (vitals.circumference?.Value) {
          vitalEntries.push(`Circumference: ${vitals.circumference.Value}`);
        }

        return vitalEntries.join(", ");
      }
      return value;
    } catch {
      return value;
    }
  };
  const parseAppointmentDescription = (value: string): string => {
    try {
      const appointment = JSON.parse(value);
      const start = appointment.startDate
        ? new Date(appointment.startDate).toLocaleString("en-GB", {
            dateStyle: "medium",
            timeStyle: "short",
          })
        : "";
      const end = appointment.endDate
        ? new Date(appointment.endDate).toLocaleString("en-GB", {
            dateStyle: "medium",
            timeStyle: "short",
          })
        : "";
      const duration =
        appointment.duration && appointment.durationUnit
          ? `${appointment.duration}${appointment.durationUnit}`
          : "";
      const confirmation = appointment.confirmationMethod
        ? `Confirmation: ${appointment.confirmationMethod}`
        : "";
      const email = appointment.email ? `Email: ${appointment.email}` : "";
      const mobile = appointment.mobile ? `Mobile: ${appointment.mobile}` : "";

      return [
        `Appointment`,
        `Start: ${start}`,
        `End: ${end}`,
        `Duration: ${duration}`,
        confirmation,
        email,
        mobile,
      ]
        .filter(Boolean)
        .join("\n");
    } catch (error) {
      console.warn("Failed to parse appointment data:", error, "Value:", value);
      return "Appointment booked";
    }
  };
  const parsePrescriptionDescription = (value: string): string => {
    try {
      const parsed = JSON.parse(value);
      if (
        parsed.prescriptionsDetails &&
        Array.isArray(parsed.prescriptionsDetails)
      ) {
        return parsed.prescriptionsDetails
          .map((item: any) => {
            const name = item.value || "Unknown medicine";
            const dose = item.dose ? `Dose: ${item.dose}` : "";
            const quantity = item.quantity ? `Qty: ${item.quantity}` : "";
            return [name, dose, quantity].filter(Boolean).join(", ");
          })
          .join("\n");
      }
      return "Prescription recorded";
    } catch (error) {
      console.warn(
        "Failed to parse prescription data:",
        error,
        "Value:",
        value,
      );
    }
  };

  const parseTasksDescription = (value: string): string => {
    if (/Person\s+\d+\s*-/.test(value)) {
      const segments = value.split(" | ");

      const texts = segments.map((segment) => {
        const dashIndex = segment.lastIndexOf("- ");
        if (dashIndex !== -1) {
          return segment.slice(dashIndex + 2).trim();
        }
        return segment.trim();
      });
      return texts.filter(Boolean).join(" ") || "Transcription recorded";
    }

    try {
      const parsed = JSON.parse(value);
      if (parsed?.tasks && Array.isArray(parsed.tasks)) {
        const taskEntries = parsed.tasks
          .map((task: any) => task.label || "Unknown task")
          .join("\n");

        return taskEntries || "Tasks recorded";
      }
      return "Tasks recorded";
    } catch {
      return value || "Tasks recorded";
    }
  };

  const parseImageDescription = (value: string): string => {
    return "Image Recorded";
  };

  const parsePrescriptionDraftDescription = (value: string): string => {
    try {
      const parsed = JSON.parse(value);
      if (
        parsed.prescriptionsDetails &&
        Array.isArray(parsed.prescriptionsDetails)
      ) {
        return parsed.prescriptionsDetails
          .map((item: any) => {
            const name = item.value || "Unknown medicine";
            const dose = item.dose ? `Dose: ${item.dose}` : "";
            const quantity = item.quantity ? `Qty: ${item.quantity}` : "";
            return [name, dose, quantity].filter(Boolean).join(", ");
          })
          .join("\n");
      }
      return "Prescription draft created";
    } catch (error) {
      console.warn(
        "Failed to parse prescription data:",
        error,
        "Value:",
        value,
      );
    }
  };

  const parseVaccinationDescription = (value: string): string => {
    try {
      const parsed = JSON.parse(value);

      const vaccines: any[] = Array.isArray(parsed.vaccinationDetails)
        ? parsed.vaccinationDetails
        : Array.isArray(parsed.vaccinationDetails?.vaccinations)
          ? parsed.vaccinationDetails.vaccinations
          : [];

      if (vaccines.length > 0) {
        const vaccineEntries: string[] = [];

        for (const vaccine of vaccines) {
          const name = vaccine.value || vaccine.vaccineName || vaccine.brandName || "";
          if (name.trim() === "") continue;

          const details: string[] = [];
          if (vaccine.dose?.trim()) details.push(`Dose: ${vaccine.dose}`);
          if (vaccine.schedule?.trim()) details.push(`Schedule: ${vaccine.schedule}`);
          if (vaccine.route?.trim()) details.push(`Route: ${vaccine.route}`);
          if (vaccine.site?.trim()) details.push(`Site: ${vaccine.site}`);
          if (vaccine.manufacturer?.trim()) details.push(`Manufacturer: ${vaccine.manufacturer}`);
          if (vaccine.batchNumber?.trim()) details.push(`Batch: ${vaccine.batchNumber}`);
          if (vaccine.expiryDate?.trim()) details.push(`Expiry: ${vaccine.expiryDate}`);

          if (details.length > 0) {
            vaccineEntries.push(
              `Vaccination: ${name}\n${details
                .map((detail) => ` ${detail}`)
                .join("\n")}`,
            );
          } else {
            vaccineEntries.push(`Vaccination: ${name}`);
          }
        }

        if (vaccineEntries.length > 0) {
          return vaccineEntries.join("\n\n");
        }
      }

      return "Vaccination recorded";
    } catch (error) {
      console.warn("Failed to parse vaccination data:", error, "Value:", value);
      return "Vaccination recorded";
    }
  };

  const parseActionPerformedDescription = (value: string): string => {
    if (!value || typeof value !== "string") {
      return "Action performed";
    }

    try {
      const parsed = JSON.parse(value);

      if (parsed?.vaccineItems && Array.isArray(parsed.vaccineItems)) {
        const vaccineEntries = parsed.vaccineItems.map((item: any) => {
          const details: string[] = [];
          if (item.vaccineName) details.push(`Vaccine: ${item.vaccineName}`);
          if (item.dose) details.push(`Dose: ${item.dose}`);
          if (item.schedule) details.push(`Schedule: ${item.schedule}`);
          if (item.site) details.push(`Site: ${item.site}`);
          if (item.route) details.push(`Route: ${item.route}`);
          if (item.manufacturer)
            details.push(`Manufacturer: ${item.manufacturer}`);
          if (item.batchNumber) details.push(`Batch: ${item.batchNumber}`);
          if (item.expiryDate) details.push(`Expiry: ${item.expiryDate}`);
          return details.filter(Boolean).join(" | ");
        });

        if (vaccineEntries.length > 0) {
          return vaccineEntries.join("\n");
        }
      }

      if (
        parsed?.calculatorResults &&
        Array.isArray(parsed.calculatorResults)
      ) {
        const calculatorEntries = parsed.calculatorResults.map((item: any) => {
          const details: string[] = [];
          if (item.medication) details.push(`Medication: ${item.medication}`);
          if (item.dosage) details.push(`Dosage: ${item.dosage}`);
          if (item.quantity) details.push(`Qty: ${item.quantity}`);
          if (item.duration) details.push(`Duration: ${item.duration}`);
          if (item.type) details.push(`Type: ${item.type}`);
          return details.filter(Boolean).join(" | ");
        });

        if (calculatorEntries.length > 0) {
          return calculatorEntries.join("\n");
        }
      }

      if (parsed?.description && typeof parsed.description === "string") {
        return parsed.description;
      }

      if (parsed?.notes && typeof parsed.notes === "string") {
        return parsed.notes;
      }

      return "Action performed";
    } catch (error) {
      // leave regular text unchanged, but avoid showing raw JSON as unformatted object,
      // i.e. if non-JSON, it is already user-friendly text.
      return value;
    }
  };

  const parseTreatmentPlanDescription = (value: string): string => {
    try {
      const parsed = JSON.parse(value);

      const formatDateTime = (input?: string) => {
        if (!input) return "";
        const d = new Date(input);
        if (isNaN(d.getTime())) return "";
        return d.toLocaleString("en-GB", {
          dateStyle: "medium",
          timeStyle: "short",
        });
      };

      const planName = parsed?.name || "Unnamed plan";
      const status = parsed?.status || "";
      const start = formatDateTime(parsed?.startDate);
      const end = formatDateTime(parsed?.endDate);
      const goalsCount = Array.isArray(parsed?.goals) ? parsed.goals.length : 0;
      const followUpsCount = Array.isArray(parsed?.followUpAppointments)
        ? parsed.followUpAppointments.length
        : 0;

      return [
        `Treatment Plan: ${planName}`,
        status ? `Status: ${status}` : "",
        start ? `Start: ${start}` : "",
        end ? `End: ${end}` : "",
        `Goals: ${goalsCount}`,
        `Follow-ups: ${followUpsCount}`,
      ]
        .filter(Boolean)
        .join("\n");
    } catch (error) {
      console.warn(
        "Failed to parse treatment plan data:",
        error,
        "Value:",
        value,
      );
      return "Treatment plan updated";
    }
  };

  const parseDocumentDescription = (value: string): string => {
    try {
      const parsed = JSON.parse(value);

      const name = parsed?.customName || parsed?.name || "Document";

      return `${name} uploaded`;
    } catch (error) {
      console.warn("Failed to parse document data:", error, "Value:", value);
      return "Document uploaded";
    }
  };

  const cleanChatMessage = (message: string): string => {
    if (!message) return message;
    const jsonPattern =
      /\s*\{[\s\S]*"Actions?"\s*:\s*\[[\s\S]*?\][\s\S]*?\}\s*$/;
    return message.replace(jsonPattern, "").trim();
  };

  const formatActorName = (actorName: string, eventName: string): string => {
    if (eventName === "Chat Response") {
      return "AI Assistant";
    }
    if (eventName === "Vitals Recorded") {
      return "Clinician";
    }
    return actorName;
  };
  const getEventTypeColor = (type: string, actor: string) => {
    switch (type) {
      case "Booking":
        return isDarkMode
          ? "bg-blue-900/30 text-blue-300 border-blue-700"
          : "bg-blue-100 text-blue-700 border-blue-200";
      case "Pre-consult":
        return isDarkMode
          ? "bg-purple-900/30 text-purple-300 border-purple-700"
          : "bg-purple-100 text-purple-700 border-purple-200";
      case "consultation":
        return isDarkMode
          ? "bg-green-900/30 text-green-300 border-green-700"
          : "bg-green-100 text-green-700 border-green-200";
      case "Prescribed":
        return isDarkMode
          ? "bg-orange-900/30 text-orange-300 border-orange-700"
          : "bg-orange-100 text-orange-700 border-orange-200";
      case "Payment":
        return isDarkMode
          ? "bg-emerald-900/30 text-emerald-300 border-emerald-700"
          : "bg-emerald-100 text-emerald-700 border-emerald-200";
      case "Action 11":
        return isDarkMode
          ? "bg-orange-900/30 text-orange-300 border-orange-700"
          : "bg-orange-100 text-orange-700 border-orange-200";
      case "Image":
        return isDarkMode
          ? "bg-indigo-900/30 text-indigo-300 border-indigo-700"
          : "bg-indigo-100 text-indigo-700 border-indigo-200";
      case "Tasks":
        return isDarkMode
          ? "bg-gray-800 text-gray-300 border-gray-600"
          : "bg-gray-100 text-gray-700 border-gray-300";
      case "Treatment Plan":
        return isDarkMode
          ? "bg-teal-900/30 text-teal-300 border-teal-700"
          : "bg-teal-100 text-teal-700 border-teal-200";
      default:
        return isDarkMode
          ? "bg-gray-800 text-gray-300 border-gray-600"
          : "bg-gray-100 text-gray-700 border-gray-300";
    }
  };

  const parseTravelHealthAuditSummary = (value: string): string | null => {
    try {
      let parsed = JSON.parse(value);
      if (typeof parsed === "string") parsed = JSON.parse(parsed);
      if (parsed?._type !== "travel-health-consultation") return null;

      const lines: string[] = [];
      lines.push("Travel Health Consultation");

      const plan = parsed.plan;
      if (plan) {
        if (Array.isArray(plan.tripDestinations) && plan.tripDestinations.length > 0) {
          lines.push(`Destinations: ${plan.tripDestinations.join(", ")}`);
        }
        if (plan.departureDate) {
          try {
            const d = new Date(plan.departureDate);
            lines.push(
              `Departure: ${d.toLocaleDateString("en-GB", { dateStyle: "medium" })}`,
            );
          } catch {}
        }
      }

      if (Array.isArray(parsed.doseAuditRecords) && parsed.doseAuditRecords.length > 0) {
        lines.push("Vaccines/Supplies administered:");
        for (const rec of parsed.doseAuditRecords) {
          const parts: string[] = [];
          if (rec.brandName) parts.push(rec.brandName);
          if (rec.doseNumber) parts.push(`Dose ${rec.doseNumber}`);
          if (rec.serviceType) parts.push(rec.serviceType);
          if (rec.batchNumber) parts.push(`Batch: ${rec.batchNumber}`);
          if (rec.injectionSite) parts.push(`Site: ${rec.injectionSite}`);
          lines.push(`  • ${parts.join(" | ")}`);
        }
      }

      if (Array.isArray(parsed.visits) && parsed.visits.length > 0) {
        lines.push(`Visits: ${parsed.visits.length}`);
      }

      return lines.join("\n");
    } catch {
      return null;
    }
  };

  const parsePathwayAuditSummary = (value: string): string | null => {
    try {
      const parsed = JSON.parse(value);
      if (!parsed?.pathwayId || !parsed?.pathwayName) return null;

      const lines: string[] = [];

      lines.push(`Pathway: ${parsed.pathwayName}`);

      if (parsed.finalOutcome?.title) {
        lines.push(`Outcome: ${parsed.finalOutcome.title}`);
      }
      if (parsed.finalOutcome?.reason) {
        lines.push(`Reason: ${parsed.finalOutcome.reason}`);
      }

      if (parsed.supplyDetails?.drugId) {
        const supply = parsed.supplyDetails;
        if (supply.quantity?.label) {
          lines.push(`Supply: ${supply.quantity.label}`);
        }
        if (supply.instructionText) {
          lines.push(`Directions: ${supply.instructionText}`);
        }
        if (supply.totalDurationDays) {
          lines.push(`Duration: ${supply.totalDurationDays} days`);
        }
      }

      if (parsed.contextSnapshot) {
        const ctx = parsed.contextSnapshot;
        const vitals: string[] = [];
        if (ctx.heightCm) vitals.push(`Height: ${ctx.heightCm} cm`);
        if (ctx.weightKg) vitals.push(`Weight: ${ctx.weightKg} kg`);
        if (ctx.bmi) vitals.push(`BMI: ${ctx.bmi}`);
        if (vitals.length > 0) {
          lines.push(`Vitals: ${vitals.join(", ")}`);
        }
      }

      if (
        Array.isArray(parsed.finalOutcome?.safetyNetting) &&
        parsed.finalOutcome.safetyNetting.length > 0
      ) {
        lines.push("Safety Netting:");
        parsed.finalOutcome.safetyNetting.forEach((item: string) => {
          lines.push(`  • ${item}`);
        });
      }

      if (Array.isArray(parsed.chainSections) && parsed.chainSections.length > 0) {
        const sectionLabels = parsed.chainSections
          .map((s: any) => s.label)
          .filter(Boolean);
        if (sectionLabels.length > 0) {
          lines.push(`Sections completed: ${sectionLabels.join(" → ")}`);
        }
      }

      if (parsed.completedAt) {
        try {
          const d = new Date(parsed.completedAt);
          lines.push(
            `Completed: ${d.toLocaleString("en-GB", {
              dateStyle: "medium",
              timeStyle: "short",
            })}`,
          );
        } catch {}
      }

      return lines.join("\n");
    } catch {
      return null;
    }
  };

  const getActorIcon = (actor: string) => {
    return actor === "patient" ? User : Calendar;
  };
  useEffect(() => {
    if (consultationAuditTimeline) {
      const timelineData = consultationAuditTimeline as any;

      const transformedEvents =
        timelineData?.timelineEvent?.map((event: any) => {
          let description = event?.value || "";
          // Handle appointment records

          if (event?.action === 4) {
            try {
              description = parseAppointmentDescription(event.value);
            } catch (error) {
              console.warn("Failed to parse appointment data:", error);
            }
          }

          // Handle vitals records
          if (event?.action === 1) {
            try {
              description = parsePrescriptionDescription(event.value);
            } catch (error) {
              console.warn("Failed to parse prescription data:", error);
            }
          }

          // Handle Image records
          if (event?.action === 13) {
            try {
              description = parseImageDescription(event.value);
            } catch (error) {
              console.warn("Failed to parse image data:", error);
            }
          }

          if (event?.action === 5) {
            try {
              const parsed = JSON.parse(event.value);
              if (parsed?.vitalsDetails) {
                description = parseVitalsDescription(event.value);
              }
            } catch (error) {
              console.warn("Failed to parse vitals data:", error);
            }
          }
          if (event?.action === 6) {
            try {
              description = parseVaccinationDescription(event.value);
            } catch (error) {
              console.warn("Failed to parse vaccination data:", error);
            }
          }
          // Handle document records
          if (event?.action === 8) {
            try {
              description = parseDocumentDescription(event.value);
            } catch (error) {
              console.warn("Failed to parse document data:", error);
            }
          }

          if (event?.action === 11) {
            try {
              description = parsePrescriptionDraftDescription(event.value);
            } catch (error) {
              console.warn("Failed to parse prescription draft data:", error);
            }
          }

          if (event?.action === 12) {
            try {
              description = parseActionPerformedDescription(event.value);
            } catch (error) {
              console.warn("Failed to parse action performed data:", error);
            }
          }

          if (event?.action === 14 || event?.event === 14) {
            try {
              description = parseTasksDescription(event.value);
            } catch (error) {
              console.warn("Failed to parse tasks data:", error);
            }
          }

          if (event?.action === 15) {
            try {
              description = parseTreatmentPlanDescription(event.value);
            } catch (error) {
              console.warn("Failed to parse treatment plan data:", error);
            }
          }

          if (event?.event === 16) {
            const pathwayText = parsePathwayAuditSummary(description);
            if (pathwayText) {
              description = pathwayText;
            } else {
              const travelHealthText = parseTravelHealthAuditSummary(description);
              if (travelHealthText) {
                description = travelHealthText;
              }
            }
          }

          // Clean chat responses
          if (event?.event === 11) {
            description = cleanChatMessage(description);
          }

          const eventName = getEventName(event?.event);
          const formattedActorName = formatActorName(
            event?.createdByName || event?.createdBy || "Unknown",
            eventName,
          );

          return {
            id: event?._id || "",
            timestamp: event?.createdOn || "",
            time: formatTime(event?.createdOn),
            date: formatDate(event?.createdOn),
            actor: "pharmacist",
            actorName: formattedActorName,
            event: eventName,
            description: description,
            type: getTypeName(event?.action, event?.event),
            transcription: event?.transcription || "",
          };
        }) || [];

      setEvents(transformedEvents);
    }
  }, [consultationAuditTimeline]);

  const formatSummaryText = (text) => {
    if (!text) return text;
    let formattedText = text
      .replace(/\s*(snomedCodes|SnomedCodes):\s*(\[.*?\]|null)\s*$/s, "")
      .replace(/\\n/g, "\n")
      .replace(/([a-zA-Z ]+:)\s*/g, "$1\n")
      .replace(/\n{3,}/g, "\n\n")
      .replace(/"/g, "")
      .trim();
    return formattedText;
  };
  return {
    events,
    getActorIcon,
    getEventTypeColor,
    auditTimelineLoader,
    formatSummaryText,
  };
};
